import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useOrgPermissions } from '@/hooks/useOrgPermissions';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { SubscriptionLimitAlert } from './SubscriptionLimitAlert';
import ValeTransporteForm, { ValeTransporteData } from './ValeTransporteForm';

interface ContratacaoFormTabsProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ModeloContrato {
  id: string;
  nome_modelo: string;
}

interface ModeloProposta {
  id: string;
  nome_modelo: string;
}

interface Cargo {
  id: string;
  nome: string;
  departamento?: string;
}

interface FormData {
  // Dados do contratado
  email: string;
  nome_completo: string;
  cpf: string;
  celular: string;
  cargo_id: string;
  department: string;
  salario: string;
  jornada_trabalho: string;
  horario_entrada: string;
  horario_saida: string;
  data_inicio: string;
  
  // Dados do Contratante / Empregador
  razao_social: string;
  cnpj: string;
  
  // Dados da Preposta Responsável
  nome_preposta: string;
  cpf_preposta: string;
  email_preposta: string;
  numero_preposta: string;
  
  // Dados do responsável (para menores de idade)
  menor_idade: boolean;
  tipo_responsavel: string;
  nome_responsavel: string;
  cpf_responsavel: string;
  email_responsavel: string;
  descricao_outro_responsavel: string;
  
  // Proposta
  modelos_proposta_selecionados: string[];
  
  // Vale Transporte - Campos detalhados
  valeTransporteData: ValeTransporteData;
  
  // Exame
  tipo_exame: string;
  data_exame: string;
  observacoes_exame: string;
  
  // Contrato
  modelos_selecionados: string[];
  tipo_contrato: string;
  observacoes_contrato: string;
  
  // Integração
  quem_recebera_contratado: string;
  observacoes: string;
}

export default function ContratacaoFormTabs({ onClose, onSuccess }: ContratacaoFormTabsProps) {
  const { toast } = useToast();
  const { userOrgInfo } = useOrgPermissions();
  const { canCreate: canCreateWithLimit, refetch: refetchLimits } = useSubscriptionLimits();
  const [activeTab, setActiveTab] = useState("dados");
  const [contratacaoId, setContratacaoId] = useState<string | null>(null);
  const [contratacaoStage, setContratacaoStage] = useState<string>('inicial');
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [modelosPropostas, setModelosPropostas] = useState<ModeloProposta[]>([]);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [propostaPersonalizada, setPropostaPersonalizada] = useState<string>('');
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    nome_completo: '',
    cpf: '',
    celular: '',
    cargo_id: '',
    department: '',
    salario: '',
    jornada_trabalho: '',
    horario_entrada: '',
    horario_saida: '',
    data_inicio: '',
    razao_social: '',
    cnpj: '',
    nome_preposta: '',
    cpf_preposta: '',
    email_preposta: '',
    numero_preposta: '',
    menor_idade: false,
    tipo_responsavel: '',
    nome_responsavel: '',
    cpf_responsavel: '',
    email_responsavel: '',
    descricao_outro_responsavel: '',
    modelos_proposta_selecionados: [],
    valeTransporteData: {
      ida_integracao_onibus_metro: false,
      ida_integracao_onibus_metro_valor: 0,
      ida_integracao_onibus_trem: false,
      ida_integracao_onibus_trem_valor: 0,
      ida_onibus_municipal: false,
      ida_onibus_municipal_valor: 0,
      ida_onibus_intermunicipal: false,
      ida_onibus_intermunicipal_valor: 0,
      ida_metro_trem: false,
      ida_metro_trem_valor: 0,
      ida_outros: false,
      ida_outros_descricao: '',
      ida_outros_valor: 0,
      volta_integracao_onibus_metro: false,
      volta_integracao_onibus_metro_valor: 0,
      volta_integracao_onibus_trem: false,
      volta_integracao_onibus_trem_valor: 0,
      volta_onibus_municipal: false,
      volta_onibus_municipal_valor: 0,
      volta_onibus_intermunicipal: false,
      volta_onibus_intermunicipal_valor: 0,
      volta_metro_trem: false,
      volta_metro_trem_valor: 0,
      volta_outros: false,
      volta_outros_descricao: '',
      volta_outros_valor: 0,
      tipo_cartao: '',
      tipo_cartao_outros: ''
    },
    tipo_exame: '',
    data_exame: '',
    observacoes_exame: '',
    modelos_selecionados: [],
    tipo_contrato: '',
    observacoes_contrato: '',
    quem_recebera_contratado: '',
    observacoes: ''
  });

  useEffect(() => {
    fetchModelos();
    fetchModelosPropostas();
    fetchCargos();
    fetchDepartamentos();
  }, [userOrgInfo?.orgId]);

  const fetchDepartamentos = async () => {
    try {
      // Departamentos fixos padrão
      const departamentosFixos = ['RH/DP', 'Limpeza', 'Recepção', 'Eventos', 'Comercial', 'Marketing', 'Jurídico'];
      
      let query = supabase
        .from('cargos')
        .select('departamento')
        .eq('status', 'ativo')
        .not('departamento', 'is', null);

      if (userOrgInfo?.orgId) {
        query = query.eq('org_id', userOrgInfo.orgId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Extrair departamentos únicos do banco
      const departamentosDoBanco = data?.map(item => item.departamento).filter(Boolean) as string[];
      
      // Combinar departamentos fixos com os do banco, removendo duplicatas
      const todosDepartamentos = [...new Set([...departamentosFixos, ...departamentosDoBanco])];
      setDepartamentos(todosDepartamentos.sort());
    } catch (error: any) {
      console.error('Erro ao carregar departamentos:', error.message);
    }
  };

  const fetchModelos = async () => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;
      setModelos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchModelosPropostas = async () => {
    try {
      const { data, error } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;
      setModelosPropostas(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchCargos = async () => {
    try {
      let query = supabase
        .from('cargos')
        .select('id, nome')
        .eq('status', 'ativo')
        .order('nome');

      // Filtrar por organização se o usuário pertence a uma
      if (userOrgInfo?.orgId) {
        query = query.eq('org_id', userOrgInfo.orgId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCargos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar cargos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const isTabValid = (tab: string): boolean => {
    switch (tab) {
      case 'dados':
        const baseDados = !!(formData.email && formData.nome_completo && formData.cpf && formData.cargo_id && formData.department && formData.salario && formData.jornada_trabalho && formData.horario_entrada && formData.horario_saida && formData.data_inicio && formData.razao_social && formData.cnpj && formData.nome_preposta && formData.cpf_preposta && formData.email_preposta && formData.numero_preposta);
        
        if (!formData.menor_idade) {
          return baseDados;
        }
        
        // Se menor de idade, validar também os campos do responsável
        const responsavelValido = !!(formData.tipo_responsavel && formData.nome_responsavel && formData.cpf_responsavel && formData.email_responsavel);
        const outroResponsavelValido = formData.tipo_responsavel !== 'Outro' || !!formData.descricao_outro_responsavel;
        
        return baseDados && responsavelValido && outroResponsavelValido;
      case 'proposta':
        return formData.modelos_proposta_selecionados.length > 0;
      case 'vale_transporte':
        // Validar apenas se algum meio de transporte foi selecionado
        const temTransporte = 
          formData.valeTransporteData.ida_integracao_onibus_metro ||
          formData.valeTransporteData.ida_integracao_onibus_trem ||
          formData.valeTransporteData.ida_onibus_municipal ||
          formData.valeTransporteData.ida_onibus_intermunicipal ||
          formData.valeTransporteData.ida_metro_trem ||
          formData.valeTransporteData.ida_outros ||
          formData.valeTransporteData.volta_integracao_onibus_metro ||
          formData.valeTransporteData.volta_integracao_onibus_trem ||
          formData.valeTransporteData.volta_onibus_municipal ||
          formData.valeTransporteData.volta_onibus_intermunicipal ||
          formData.valeTransporteData.volta_metro_trem ||
          formData.valeTransporteData.volta_outros;
        return temTransporte;
      case 'exame':
        return !!formData.tipo_exame;
      case 'contrato':
        return formData.modelos_selecionados.length > 0;
      case 'integracao':
        return !!formData.quem_recebera_contratado;
      default:
        return false;
    }
  };

  const canAccessTab = (tab: string): boolean => {
    // Todas as abas sempre acessíveis
    return true;
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleModeloToggle = (modeloId: string) => {
    setFormData(prev => ({
      ...prev,
      modelos_selecionados: prev.modelos_selecionados.includes(modeloId)
        ? prev.modelos_selecionados.filter(id => id !== modeloId)
        : [...prev.modelos_selecionados, modeloId]
    }));
  };

  const handleModeloPropostaToggle = (modeloId: string) => {
    setFormData(prev => ({
      ...prev,
      modelos_proposta_selecionados: prev.modelos_proposta_selecionados.includes(modeloId)
        ? prev.modelos_proposta_selecionados.filter(id => id !== modeloId)
        : [...prev.modelos_proposta_selecionados, modeloId]
    }));
  };

  // Atualizar preview automaticamente quando a seleção mudar
  useEffect(() => {
    if (formData.modelos_proposta_selecionados.length > 0) {
      handleModeloPropostaChange();
    } else {
      setPropostaPersonalizada('');
    }
  }, [formData.modelos_proposta_selecionados]);

  const saveStep = async (step: string) => {
    try {
      setLoading(true);
      
      if (!contratacaoId && step === 'dados') {
        // Verificar se já existe uma contratação com este email na mesma organização
        const { data: existingContratacao } = await supabase
          .from('contratacao')
          .select('id, stage, org_id')
          .eq('email', formData.email)
          .eq('org_id', userOrgInfo?.orgId)
          .maybeSingle();

        if (existingContratacao) {
          // Se já existe, usar a contratação existente
          setContratacaoId(existingContratacao.id);
          setContratacaoStage(existingContratacao.stage);
          
          toast({
            title: "Contratação encontrada",
            description: "Uma contratação para este email já existe. Usando dados existentes.",
          });
        } else {
          // Verificar limites antes de criar nova contratação
          if (!canCreateWithLimit) {
            toast({
              title: "Limite atingido",
              description: "Você atingiu o limite de contratações do seu plano atual.",
              variant: "destructive"
            });
            return;
          }

          // Criar nova contratação
          const { data, error } = await supabase
            .from('contratacao')
            .insert([{
              email: formData.email,
              full_name: formData.nome_completo,
              nome_completo: formData.nome_completo,
              cpf: formData.cpf,
              celular: formData.celular,
              cargo_id: formData.cargo_id || null,
              department: formData.department || null,
              salario: formData.salario ? parseFloat(formData.salario) : null,
              jornada_trabalho: formData.jornada_trabalho,
              horario_entrada: formData.horario_entrada,
              horario_saida: formData.horario_saida,
              data_inicio: formData.data_inicio,
              razao_social: formData.razao_social,
              cnpj: formData.cnpj,
              nome_preposta: formData.nome_preposta,
              cpf_preposta: formData.cpf_preposta,
              email_preposta: formData.email_preposta,
              numero_preposta: formData.numero_preposta,
              menor_idade: formData.menor_idade,
              tipo_responsavel: formData.menor_idade ? formData.tipo_responsavel : null,
              nome_responsavel: formData.menor_idade ? formData.nome_responsavel : null,
              cpf_responsavel: formData.menor_idade ? formData.cpf_responsavel : null,
              email_responsavel: formData.menor_idade ? formData.email_responsavel : null,
              descricao_outro_responsavel: (formData.menor_idade && formData.tipo_responsavel === 'Outro') ? formData.descricao_outro_responsavel : null,
              stage: 'proposta',
              org_id: userOrgInfo?.orgId
            }])
            .select()
            .single();

          if (error) throw error;
          setContratacaoId(data.id);
          setContratacaoStage(data.stage);
          
          // Refetch limits para atualizar contador
          refetchLimits();
          
          toast({
            title: "Sucesso",
            description: "Dados salvos com sucesso!",
          });
        }
      } else if (contratacaoId) {
        // Atualizar contratação existente
        const updateData: any = {};
        
        if (step === 'dados') {
          updateData.email = formData.email;
          updateData.full_name = formData.nome_completo;
          updateData.nome_completo = formData.nome_completo;
          updateData.cpf = formData.cpf;
          updateData.celular = formData.celular;
          updateData.cargo_id = formData.cargo_id || null;
          updateData.department = formData.department || null;
          updateData.salario = formData.salario ? parseFloat(formData.salario) : null;
          updateData.jornada_trabalho = formData.jornada_trabalho;
          updateData.horario_entrada = formData.horario_entrada;
          updateData.horario_saida = formData.horario_saida;
          updateData.data_inicio = formData.data_inicio;
          updateData.razao_social = formData.razao_social;
          updateData.cnpj = formData.cnpj;
          updateData.nome_preposta = formData.nome_preposta;
          updateData.cpf_preposta = formData.cpf_preposta;
          updateData.email_preposta = formData.email_preposta;
          updateData.numero_preposta = formData.numero_preposta;
          updateData.menor_idade = formData.menor_idade;
          updateData.tipo_responsavel = formData.menor_idade ? formData.tipo_responsavel : null;
          updateData.nome_responsavel = formData.menor_idade ? formData.nome_responsavel : null;
          updateData.cpf_responsavel = formData.menor_idade ? formData.cpf_responsavel : null;
          updateData.email_responsavel = formData.menor_idade ? formData.email_responsavel : null;
          updateData.descricao_outro_responsavel = (formData.menor_idade && formData.tipo_responsavel === 'Outro') ? formData.descricao_outro_responsavel : null;
        }
        
        if (step === 'proposta') {
          updateData.ids_modelos_proposta = formData.modelos_proposta_selecionados;
        }
        
        if (step === 'vale_transporte') {
          // Buscar dados do usuário atual
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          
          // Calcular totais
          const totalIda = 
            (formData.valeTransporteData.ida_integracao_onibus_metro ? formData.valeTransporteData.ida_integracao_onibus_metro_valor : 0) +
            (formData.valeTransporteData.ida_integracao_onibus_trem ? formData.valeTransporteData.ida_integracao_onibus_trem_valor : 0) +
            (formData.valeTransporteData.ida_onibus_municipal ? formData.valeTransporteData.ida_onibus_municipal_valor : 0) +
            (formData.valeTransporteData.ida_onibus_intermunicipal ? formData.valeTransporteData.ida_onibus_intermunicipal_valor : 0) +
            (formData.valeTransporteData.ida_metro_trem ? formData.valeTransporteData.ida_metro_trem_valor : 0) +
            (formData.valeTransporteData.ida_outros ? formData.valeTransporteData.ida_outros_valor : 0);

          const totalVolta = 
            (formData.valeTransporteData.volta_integracao_onibus_metro ? formData.valeTransporteData.volta_integracao_onibus_metro_valor : 0) +
            (formData.valeTransporteData.volta_integracao_onibus_trem ? formData.valeTransporteData.volta_integracao_onibus_trem_valor : 0) +
            (formData.valeTransporteData.volta_onibus_municipal ? formData.valeTransporteData.volta_onibus_municipal_valor : 0) +
            (formData.valeTransporteData.volta_onibus_intermunicipal ? formData.valeTransporteData.volta_onibus_intermunicipal_valor : 0) +
            (formData.valeTransporteData.volta_metro_trem ? formData.valeTransporteData.volta_metro_trem_valor : 0) +
            (formData.valeTransporteData.volta_outros ? formData.valeTransporteData.volta_outros_valor : 0);

          const total = totalIda + totalVolta;
          
          // Validar se o total excede R$ 21,00
          if (total > 21) {
            const confirmar = window.confirm(
              `O valor total do vale transporte (R$ ${total.toFixed(2)}) excede o limite esperado de R$ 21,00. Deseja continuar?`
            );
            if (!confirmar) {
              setLoading(false);
              return;
            }
          }

          // Salvar totais na tabela contratacao (compatibilidade)
          updateData.vale_transporte_valor_ida = totalIda;
          updateData.vale_transporte_valor_volta = totalVolta;
          
          // Salvar dados detalhados na tabela vale_transporte_detalhado
          const valeTransporteDetalhado = {
            contratacao_id: contratacaoId,
            user_id: userData.user?.id,
            org_id: userOrgInfo?.orgId,
            ...formData.valeTransporteData,
            total_ida: totalIda,
            total_volta: totalVolta,
            total_diario: total,
            created_by: userData.user?.id,
            updated_by: userData.user?.id
          };

          // Verificar se já existe registro
          const { data: existingVT } = await supabase
            .from('vale_transporte_detalhado')
            .select('id')
            .eq('contratacao_id', contratacaoId)
            .maybeSingle();

          if (existingVT) {
            // Atualizar
            const { error: vtError } = await supabase
              .from('vale_transporte_detalhado')
              .update(valeTransporteDetalhado)
              .eq('id', existingVT.id);

            if (vtError) {
              console.error('Erro ao atualizar vale_transporte_detalhado:', vtError);
            }
          } else {
            // Inserir
            const { error: vtError } = await supabase
              .from('vale_transporte_detalhado')
              .insert(valeTransporteDetalhado);

            if (vtError) {
              console.error('Erro ao inserir vale_transporte_detalhado:', vtError);
            }
          }
        }
        
        if (step === 'contrato') {
          updateData.ids_modelos_contrato = formData.modelos_selecionados;
        }
        
        if (step === 'exame') {
          updateData.stage = 'exame_admissional';
        }

        const { error } = await supabase
          .from('contratacao')
          .update(updateData)
          .eq('id', contratacaoId);

        if (error) throw error;
        
        toast({
          title: "Sucesso",
          description: "Alterações salvas com sucesso!",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const personalizarProposta = async () => {
    if (formData.modelos_proposta_selecionados.length === 0) return '';

    try {
      // Buscar TODOS os modelos selecionados
      const { data: modelosData, error: modelosError } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo, conteudo_html')
        .in('id', formData.modelos_proposta_selecionados);

      if (modelosError) throw modelosError;
      if (!modelosData || modelosData.length === 0) return '';

      // Buscar informações do cargo
      let cargo = null;
      if (formData.cargo_id) {
        const { data: cargoData, error: cargoError } = await supabase
          .from('cargos')
          .select('nome, departamento')
          .eq('id', formData.cargo_id)
          .maybeSingle();

        if (!cargoError && cargoData) {
          cargo = cargoData;
        }
      }

      // Substituir marcadores
      const hoje = new Date();
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      // Formatar data de início
      let dataInicioFormatada = formData.data_inicio || 'A definir';
      let dataInicioExtenso = 'A definir';
      let diaInicio = '';
      let mesInicio = '';
      let anoInicio = '';
      
      if (formData.data_inicio) {
        const [ano, mes, dia] = formData.data_inicio.split('-').map(Number);
        dataInicioFormatada = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
        mesInicio = meses[mes - 1];
        dataInicioExtenso = `${dia} de ${mesInicio} de ${ano}`;
        diaInicio = dia.toString();
        anoInicio = ano.toString();
      }

      // Processar TODOS os modelos e concatenar
      const propostasProcessadas = modelosData.map((modelo, index) => {
        let propostaPersonalizada = modelo.conteudo_html;

        // PROCESSAR PADRÕES COMPOSTOS PRIMEIRO (evita repetição)
        propostaPersonalizada = propostaPersonalizada
          .replace(/@data@\s+de\s+@mes@\s+de\s+@ano@/gi, 
            `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia_inicio@\s+de\s+@mes_inicio@\s+de\s+@ano_inicio@/gi, 
            dataInicioExtenso);

        // DEPOIS substituir variáveis individuais
        propostaPersonalizada = propostaPersonalizada
          .replace(/@nome_completo@/g, formData.nome_completo || 'Não informado')
          .replace(/@dataInicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio_extenso@/gi, dataInicioExtenso)
          .replace(/@dia_inicio@/gi, diaInicio)
          .replace(/@mes_inicio@/gi, mesInicio)
          .replace(/@ano_inicio@/gi, anoInicio)
          .replace(/@cargo@/g, cargo?.nome || 'Não informado')
          .replace(/@departamento@/g, formData.department || cargo?.departamento || 'Não informado')
          .replace(/@salario@/g, formData.salario ? `R$ ${parseFloat(formData.salario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'A definir')
          .replace(/@horario_entrada@/g, formData.horario_entrada || '00:00')
          .replace(/@horario_saida@/g, formData.horario_saida || '00:00')
          .replace(/@cpf@/g, formData.cpf || 'Não informado')
          .replace(/@razao_social@/g, formData.razao_social || 'Não informado')
          .replace(/@cnpj@/g, formData.cnpj || 'Não informado')
          .replace(/@data@/g, `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia@/g, hoje.getDate().toString())
          .replace(/@mes@/g, meses[hoje.getMonth()])
          .replace(/@ano@/g, hoje.getFullYear().toString());

        // Adicionar cabeçalho com nome do modelo e separador
        const cabecalho = `
          <div style="background: #f0f0f0; padding: 15px; margin: 20px 0; border-left: 4px solid #2563eb;">
            <h2 style="margin: 0; color: #1e40af;">PROPOSTA ${index + 1} de ${modelosData.length}: ${modelo.nome_modelo}</h2>
          </div>
        `;
        
        return cabecalho + propostaPersonalizada;
      });

      // Juntar todas as propostas
      return propostasProcessadas.join('<hr style="margin: 40px 0; border: 2px solid #e5e7eb;" />');
    } catch (error: any) {
      toast({
        title: "Erro ao personalizar proposta",
        description: error.message,
        variant: "destructive",
      });
      return '';
    }
  };


  const savePropostaToTable = async (htmlProposta: string, contratacaoId: string) => {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Buscar o user_id do candidato pela contratação
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('id_usuario, user_criado, email')
        .eq('id', contratacaoId)
        .single();

      // Determinar o user_contratado (candidato)
      let userContratado = contratacaoData?.id_usuario || contratacaoData?.user_criado;
      
      // Se não encontrou user_id, buscar pelo email
      if (!userContratado && contratacaoData?.email) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', contratacaoData.email)
          .maybeSingle();
        
        userContratado = profileData?.user_id;
      }

      // Verificar se já existe uma proposta para esta contratação
      const { data: existingProposta } = await supabase
        .from('contratacao_contratos')
        .select('id')
        .eq('contratacao_id', contratacaoId)
        .eq('modelo_contrato_id', null) // Proposta não tem modelo_contrato_id
        .maybeSingle();

      if (existingProposta) {
        // Atualizar proposta existente
        const { error: updateError } = await supabase
          .from('contratacao_contratos')
          .update({
            html_contrato: htmlProposta,
            updated_by: user.user?.id
          })
          .eq('id', existingProposta.id);

        if (updateError) throw updateError;
      } else {
        // Criar nova proposta na tabela contratacao_contratos
        // Buscar nome do modelo de proposta (primeiro selecionado)
        const primeiroModelo = formData.modelos_proposta_selecionados[0] || '';
        const modeloProposta = modelosPropostas.find(m => m.id === primeiroModelo);
        const nomeModeloProposta = modeloProposta?.nome_modelo || 'Proposta';
        const cpfLimpo = formData.cpf.replace(/[^\d]/g, '');
        
        const proposta = {
          contratacao_id: contratacaoId,
          user_contratado: userContratado, // Usuário que deve assinar
          modelo_contrato_id: null, // Proposta não tem modelo_contrato_id específico
          html_contrato: htmlProposta,
          bt_aceite: false,
          prazo: new Date().toISOString().split('T')[0], // Prazo de hoje
          created_by: user.user?.id, // RH que criou
          clicksign_assinado: 'pendente',
          documento_cpf_nome: `Proposta_${nomeModeloProposta.replace(/\s+/g, '_')}_${cpfLimpo}.pdf`
        };

        const { error: propostaError } = await supabase
          .from('contratacao_contratos')
          .insert(proposta);

        if (propostaError) throw propostaError;
      }
    } catch (error: any) {
      console.error('Erro ao salvar proposta na tabela contratacao_contratos:', error);
    }
  };

  const handleModeloPropostaChange = async () => {
    if (formData.modelos_proposta_selecionados.length > 0) {
      // Gerar preview da primeira proposta selecionada
      const propostaHtml = await personalizarPropostaComDados(formData);
      if (propostaHtml) {
        setPropostaPersonalizada(propostaHtml);
      }
    } else {
      setPropostaPersonalizada('');
    }
  };

  const personalizarPropostaComDados = async (dadosForm: FormData) => {
    if (dadosForm.modelos_proposta_selecionados.length === 0) return '';

    try {
      // Buscar TODOS os modelos selecionados
      const { data: modelosData, error: modelosError } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo, conteudo_html')
        .in('id', dadosForm.modelos_proposta_selecionados);

      if (modelosError) throw modelosError;
      if (!modelosData || modelosData.length === 0) return '';

      // Buscar informações do cargo se fornecido
      let cargo = null;
      if (dadosForm.cargo_id) {
        const { data: cargoData, error: cargoError } = await supabase
          .from('cargos')
          .select('nome, departamento')
          .eq('id', dadosForm.cargo_id)
          .single();

        if (!cargoError && cargoData) {
          cargo = cargoData;
        }
      }

      // Substituir marcadores
      const hoje = new Date();
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      // Formatar data de início
      let dataInicioFormatada = dadosForm.data_inicio || 'A definir';
      let dataInicioExtenso = 'A definir';
      let diaInicio = '';
      let mesInicio = '';
      let anoInicio = '';
      
      if (dadosForm.data_inicio) {
        const [ano, mes, dia] = dadosForm.data_inicio.split('-').map(Number);
        dataInicioFormatada = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
        mesInicio = meses[mes - 1];
        dataInicioExtenso = `${dia} de ${mesInicio} de ${ano}`;
        diaInicio = dia.toString();
        anoInicio = ano.toString();
      }

      // Processar TODOS os modelos e concatenar
      const propostasProcessadas = modelosData.map((modelo, index) => {
        let propostaPersonalizada = modelo.conteudo_html;

        // PROCESSAR PADRÕES COMPOSTOS PRIMEIRO (evita repetição)
        propostaPersonalizada = propostaPersonalizada
          .replace(/@data@\s+de\s+@mes@\s+de\s+@ano@/gi, 
            `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia_inicio@\s+de\s+@mes_inicio@\s+de\s+@ano_inicio@/gi, 
            dataInicioExtenso);

        // DEPOIS substituir variáveis individuais
        propostaPersonalizada = propostaPersonalizada
          .replace(/@nome_completo@/g, dadosForm.nome_completo || 'Não informado')
          .replace(/@dataInicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio_extenso@/gi, dataInicioExtenso)
          .replace(/@dia_inicio@/gi, diaInicio)
          .replace(/@mes_inicio@/gi, mesInicio)
          .replace(/@ano_inicio@/gi, anoInicio)
          .replace(/@cargo@/g, cargo?.nome || 'Não informado')
          .replace(/@departamento@/g, dadosForm.department || cargo?.departamento || 'Não informado')
          .replace(/@salario@/g, dadosForm.salario ? `R$ ${parseFloat(dadosForm.salario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'A definir')
          .replace(/@horario_entrada@/g, dadosForm.horario_entrada || '00:00')
          .replace(/@horario_saida@/g, dadosForm.horario_saida || '00:00')
          .replace(/@cpf@/g, dadosForm.cpf || 'Não informado')
          .replace(/@razao_social@/g, dadosForm.razao_social || 'Não informado')
          .replace(/@cnpj@/g, dadosForm.cnpj || 'Não informado')
          .replace(/@data@/g, `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia@/g, hoje.getDate().toString())
          .replace(/@mes@/g, meses[hoje.getMonth()])
          .replace(/@ano@/g, hoje.getFullYear().toString());

        // Adicionar cabeçalho com nome do modelo e separador
        const cabecalho = `
          <div style="background: #f0f0f0; padding: 15px; margin: 20px 0; border-left: 4px solid #2563eb;">
            <h2 style="margin: 0; color: #1e40af;">PROPOSTA ${index + 1} de ${modelosData.length}: ${modelo.nome_modelo}</h2>
          </div>
        `;
        
        return cabecalho + propostaPersonalizada;
      });

      // Juntar todas as propostas
      return propostasProcessadas.join('<hr style="margin: 40px 0; border: 2px solid #e5e7eb;" />');
    } catch (error: any) {
      toast({
        title: "Erro ao personalizar proposta",
        description: error.message,
        variant: "destructive",
      });
      return '';
    }
  };

  const handleSaveAndNext = async () => {
    await saveStep(activeTab);
    
    const tabs = ['dados', 'proposta', 'exame', 'contrato', 'integracao'];
    const currentIndex = tabs.indexOf(activeTab);
    
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handleCriarPermissao = async () => {
    try {
      setLoading(true);
      
      // Salvar HTML se ainda não foi feito
      if (!propostaPersonalizada && formData.modelos_proposta_selecionados.length > 0) {
        const propostaHtml = await personalizarProposta();
        if (propostaHtml) {
          setPropostaPersonalizada(propostaHtml);
          
          // Salvar HTML no banco
          if (contratacaoId) {
            const { error: updateError } = await supabase
              .from('contratacao')
              .update({ 
                proposta_html: propostaHtml,
                stage: 'proposta_enviada'
              } as any)
              .eq('id', contratacaoId);
              
            if (updateError) throw updateError;
            
            // Salvar também na tabela contratacao_contratos
            await savePropostaToTable(propostaHtml, contratacaoId);
          }
        }
      } else if (contratacaoId) {
        // Se a proposta já foi personalizada, apenas atualizar o stage
        const { error: updateError } = await supabase
          .from('contratacao')
          .update({ stage: 'proposta_enviada' })
          .eq('id', contratacaoId);
          
        if (updateError) throw updateError;
      }
      
      // Enviar dados para o webhook - CADA PROPOSTA COMO DOCUMENTO SEPARADO
      try {
        // Limpar números de telefone (remover parênteses, espaços e hífens)
        const limparTelefone = (telefone: string) => {
          if (!telefone) return '';
          return telefone.replace(/[^\d]/g, ''); // Remove tudo exceto dígitos
        };

        // Buscar TODAS as propostas individuais da tabela contratacao_propostas
        const { data: propostasData, error: propostasError } = await supabase
          .from('contratacao_propostas')
          .select('id, html_proposta, documento_cpf_nome, modelo_proposta_id')
          .eq('contratacao_id', contratacaoId);

        if (propostasError) {
          throw propostasError;
        }

        console.log('📋 Propostas encontradas para envio:', propostasData?.length || 0);

        if (!propostasData || propostasData.length === 0) {
          toast({
            title: "Aviso",
            description: "Nenhuma proposta encontrada para envio. Salve as propostas primeiro.",
            variant: "default",
          });
          setLoading(false);
          return;
        }

        // Enviar cada proposta individualmente (igual aos contratos)
        let envioConcluido = 0;
        let errosEnvio = 0;

        for (const proposta of propostasData) {
          console.log(`📤 Enviando proposta ${envioConcluido + 1}/${propostasData.length}: ${proposta.documento_cpf_nome}`);
          
          const webhookData = {
            nome_contratante: formData.nome_preposta,
            email_contratante: formData.email_preposta,
            numero_contratante: limparTelefone(formData.numero_preposta),
            nome_contratado: formData.nome_completo,
            email_contratado: formData.email,
            numero_contratado: limparTelefone(formData.celular),
            html_proposta: proposta.html_proposta,
            documento_cpf: proposta.documento_cpf_nome,
            proposta_id: proposta.id,
            modelo_proposta_id: proposta.modelo_proposta_id
          };

          console.log('📋 Dados enviados para proposta:', {
            ...webhookData,
            html_proposta: `[HTML ${webhookData.html_proposta?.length || 0} chars]`
          });

          try {
            // Usar edge function igual aos contratos
            const { data: response, error: invokeError } = await supabase.functions.invoke('enviar-propostas-webhook', {
              body: webhookData
            });

            if (invokeError) {
              console.error(`❌ Erro ao enviar proposta ${proposta.id}:`, invokeError);
              errosEnvio++;
              continue;
            }

            console.log('📧 RESPOSTA DO WEBHOOK:', JSON.stringify(response, null, 2));

            // Extrair document_id da resposta
            let documentId = null;
            const responseData = response?.data || response;
            
            if (responseData?.envelope_id) {
              documentId = responseData.envelope_id;
            } else if (responseData?.envelopeId) {
              documentId = responseData.envelopeId;
            } else if (responseData?.id) {
              documentId = responseData.id;
            } else if (responseData?.key) {
              documentId = responseData.key;
            } else if (responseData?.data?.envelope_id) {
              documentId = responseData.data.envelope_id;
            } else if (responseData?.data?.id) {
              documentId = responseData.data.id;
            } else if (responseData?.result?.envelope_id) {
              documentId = responseData.result.envelope_id;
            } else if (responseData?.result?.id) {
              documentId = responseData.result.id;
            } else if (responseData?.envelope?.id) {
              documentId = responseData.envelope.id;
            } else if (responseData?.document?.key) {
              documentId = responseData.document.key;
            } else {
              // Última tentativa: regex para encontrar UUID
              const responseStr = JSON.stringify(responseData);
              const envelopeMatch = responseStr.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/);
              if (envelopeMatch) {
                documentId = envelopeMatch[0];
              }
            }

            console.log(`🆔 Document ID extraído para proposta ${proposta.id}:`, documentId);

            // Salvar document_id na proposta
            if (documentId) {
              const { error: updateError } = await supabase
                .from('contratacao_propostas')
                .update({ 
                  clicksign_document_id: documentId,
                  updated_at: new Date().toISOString()
                })
                .eq('id', proposta.id);

              if (updateError) {
                console.error(`❌ Erro ao salvar document_id para proposta ${proposta.id}:`, updateError);
              } else {
                console.log(`✅ Document ID salvo para proposta ${proposta.id}`);
              }

              // Salvar primeiro document_id na tabela contratacao (compatibilidade)
              if (envioConcluido === 0) {
                await supabase
                  .from('contratacao')
                  .update({ 
                    proposta_clicksign_document_id: documentId,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', contratacaoId);
              }
            }

            envioConcluido++;
          } catch (propostaError) {
            console.error(`❌ Erro ao processar proposta ${proposta.id}:`, propostaError);
            errosEnvio++;
          }
        }

        console.log(`✅ Envio concluído: ${envioConcluido} sucesso, ${errosEnvio} erros`);
        
        if (envioConcluido > 0) {
          toast({
            title: "Sucesso",
            description: `${envioConcluido} proposta(s) enviada(s) para assinatura!${errosEnvio > 0 ? ` (${errosEnvio} erro(s))` : ''}`,
          });
        } else {
          toast({
            title: "Erro",
            description: "Nenhuma proposta foi enviada com sucesso.",
            variant: "destructive",
          });
        }
        
        // Aguardar um pouco e verificar status automaticamente
        setTimeout(async () => {
          try {
            console.log('🔍 Verificando status automaticamente após 5 segundos...');
            await supabase.functions.invoke('verificar-status-clicksign', {
              body: {}
            });
          } catch (error) {
            console.error('❌ Erro na verificação automática:', error);
          }
        }, 5000);
      } catch (webhookError) {
        console.error('Erro ao enviar webhook:', webhookError);
        toast({
          title: "Aviso",
          description: "Permissão criada, mas houve um erro ao processar a integração externa.",
          variant: "default",
        });
      }
      
      setContratacaoStage('proposta_enviada');
      
      toast({
        title: "Sucesso",
        description: "Permissão criada! O candidato poderá criar sua conta e acessar a área restrita de contratação.",
      });
      
      // Fechar o pop-up após criar a permissão
      onSuccess();
      onClose();
      
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    try {
      // Atualizar contratação com observações e finalizar
      if (contratacaoId) {
        const { error } = await supabase
          .from('contratacao')
          .update({ 
            stage: 'finalizado',
            quem_recebera_contratado: formData.quem_recebera_contratado,
            observacoes: formData.observacoes
          })
          .eq('id', contratacaoId);

        if (error) throw error;
      }

      toast({
        title: "Sucesso",
        description: "Contratação criada com sucesso!",
      });
      
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Subscription Limit Alert */}
      <SubscriptionLimitAlert showOnlyWhenBlocked />
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger 
            value="dados"
            className={isTabValid('dados') ? 'bg-green-100 text-green-800' : ''}
          >
            Dados
          </TabsTrigger>
          <TabsTrigger 
            value="proposta"
            className={isTabValid('proposta') ? 'bg-green-100 text-green-800' : ''}
          >
            Proposta
          </TabsTrigger>
          <TabsTrigger 
            value="vale_transporte"
            className={isTabValid('vale_transporte') ? 'bg-green-100 text-green-800' : ''}
          >
            Vale Transporte
          </TabsTrigger>
          <TabsTrigger 
            value="exame"
            className={isTabValid('exame') ? 'bg-green-100 text-green-800' : ''}
          >
            Exame
          </TabsTrigger>
          <TabsTrigger 
            value="contrato"
            className={isTabValid('contrato') ? 'bg-green-100 text-green-800' : ''}
          >
            Contrato
          </TabsTrigger>
          <TabsTrigger 
            value="integracao"
            className={isTabValid('integracao') ? 'bg-green-100 text-green-800' : ''}
          >
            Integração
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="space-y-4">
          {/* Seção Dados do Contratado */}
          <div className="border-b pb-4 mb-6">
            <h3 className="text-lg font-semibold mb-4">Dados do Contratado</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  maxLength={100}
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="exemplo@email.com"
                />
              </div>
              <div>
                <Label htmlFor="nome_completo">Nome Completo *</Label>
                <Input
                  id="nome_completo"
                  maxLength={100}
                  value={formData.nome_completo}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_completo: e.target.value }))}
                  placeholder="Nome completo do candidato"
                />
              </div>
              <div>
                <Label htmlFor="cpf">CPF *</Label>
                <Input
                  id="cpf"
                  maxLength={14}
                  value={formData.cpf}
                  onChange={(e) => setFormData(prev => ({ ...prev, cpf: e.target.value }))}
                  placeholder="000.000.000-00"
                />
              </div>
              <div>
                <Label htmlFor="celular">Celular</Label>
                <Input
                  id="celular"
                  type="tel"
                  maxLength={15}
                  value={formData.celular}
                  onChange={(e) => {
                    // Permitir apenas números, parênteses, hífens e espaços
                    const value = e.target.value.replace(/[^\d\(\)\-\s]/g, '');
                    setFormData(prev => ({ ...prev, celular: value }));
                  }}
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <Label htmlFor="cargo">Cargo *</Label>
                <Select
                  value={formData.cargo_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, cargo_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cargo" />
                  </SelectTrigger>
                  <SelectContent>
                    {cargos.map(cargo => (
                      <SelectItem key={cargo.id} value={cargo.id}>
                        {cargo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="department">Departamento *</Label>
                <Select
                  value={formData.department} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map(dep => (
                      <SelectItem key={dep} value={dep}>
                        {dep}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="salario">Salário *</Label>
                <Input
                  id="salario"
                  type="number"
                  step="0.01"
                  max="50000"
                  value={formData.salario}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value <= 50000 || e.target.value === '') {
                      setFormData(prev => ({ ...prev, salario: e.target.value }));
                    }
                  }}
                  placeholder="R$ 0,00"
                />
              </div>
              <div>
                <Label htmlFor="jornada">Jornada de Trabalho *</Label>
                <Select
                  value={formData.jornada_trabalho} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, jornada_trabalho: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a jornada" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="40h">40h semanais</SelectItem>
                    <SelectItem value="44h">44h semanais</SelectItem>
                    <SelectItem value="medio_periodo">Meio período</SelectItem>
                    <SelectItem value="integral">Integral</SelectItem>
                    <SelectItem value="flexivel">Flexível</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="horario_entrada">Horário de Entrada *</Label>
                  <Input
                    id="horario_entrada"
                    type="time"
                    value={formData.horario_entrada}
                    onChange={(e) => setFormData(prev => ({ ...prev, horario_entrada: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="horario_saida">Horário de Saída *</Label>
                  <Input
                    id="horario_saida"
                    type="time"
                    value={formData.horario_saida}
                    onChange={(e) => setFormData(prev => ({ ...prev, horario_saida: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="data_inicio">Data de Início *</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Seção Dados do Contratante / Empregador */}
          <div className="border-b pb-4 mb-6">
            <h3 className="text-lg font-semibold mb-4">Dados do Contratante / Empregador</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="razao_social">Razão Social *</Label>
                <Input
                  id="razao_social"
                  maxLength={200}
                  value={formData.razao_social}
                  onChange={(e) => setFormData(prev => ({ ...prev, razao_social: e.target.value }))}
                  placeholder="Razão social da empresa"
                />
              </div>
              <div>
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  maxLength={18}
                  value={formData.cnpj}
                  onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                />
              </div>
            </div>
          </div>

          {/* Seção Dados da Preposta Responsável */}
          <div className="border-b pb-4 mb-6">
            <h3 className="text-lg font-semibold mb-4">Dados da Preposta Responsável</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nome_preposta">Nome *</Label>
                <Input
                  id="nome_preposta"
                  maxLength={100}
                  value={formData.nome_preposta}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_preposta: e.target.value }))}
                  placeholder="Nome completo da preposta"
                />
              </div>
              <div>
                <Label htmlFor="cpf_preposta">CPF *</Label>
                <Input
                  id="cpf_preposta"
                  maxLength={14}
                  value={formData.cpf_preposta}
                  onChange={(e) => setFormData(prev => ({ ...prev, cpf_preposta: e.target.value }))}
                  placeholder="000.000.000-00"
                />
              </div>
              <div>
                <Label htmlFor="email_preposta">E-mail *</Label>
                <Input
                  id="email_preposta"
                  type="email"
                  maxLength={100}
                  value={formData.email_preposta}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_preposta: e.target.value }))}
                  placeholder="email@preposta.com"
                />
              </div>
              <div>
                <Label htmlFor="numero_preposta">Número *</Label>
                <Input
                  id="numero_preposta"
                  type="tel"
                  maxLength={15}
                  value={formData.numero_preposta}
                  onChange={(e) => {
                    // Permitir apenas números, parênteses, hífens e espaços
                    const value = e.target.value.replace(/[^\d\(\)\-\s]/g, '');
                    setFormData(prev => ({ ...prev, numero_preposta: value }));
                  }}
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>
          </div>

          {/* Seção Menor de Idade */}
          <div className="border-t pt-4 mt-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Informações do Responsável</h3>
              
              <div>
                <Label className="text-base font-medium">Menor de Idade? *</Label>
                <RadioGroup 
                  value={formData.menor_idade ? "sim" : "nao"} 
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    menor_idade: value === "sim",
                    // Limpar campos do responsável se não for menor de idade
                    tipo_responsavel: value === "sim" ? prev.tipo_responsavel : '',
                    nome_responsavel: value === "sim" ? prev.nome_responsavel : '',
                    cpf_responsavel: value === "sim" ? prev.cpf_responsavel : '',
                    email_responsavel: value === "sim" ? prev.email_responsavel : '',
                    descricao_outro_responsavel: value === "sim" ? prev.descricao_outro_responsavel : ''
                  }))}
                  className="flex space-x-6 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="menor-sim" />
                    <Label htmlFor="menor-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="menor-nao" />
                    <Label htmlFor="menor-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.menor_idade && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="tipo_responsavel">Tipo de Responsável *</Label>
                    <Select
                      value={formData.tipo_responsavel} 
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        tipo_responsavel: value,
                        // Limpar descrição se não for "Outro"
                        descricao_outro_responsavel: value !== 'Outro' ? '' : prev.descricao_outro_responsavel
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de responsável" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pai ou Mãe">Pai ou Mãe</SelectItem>
                        <SelectItem value="Familiar">Familiar</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.tipo_responsavel === 'Outro' && (
                    <div>
                      <Label htmlFor="descricao_outro_responsavel">Descrição do Responsável *</Label>
                      <Input
                        id="descricao_outro_responsavel"
                        value={formData.descricao_outro_responsavel}
                        onChange={(e) => setFormData(prev => ({ ...prev, descricao_outro_responsavel: e.target.value }))}
                        placeholder="Descreva o tipo de responsável"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="nome_responsavel">Nome do Responsável *</Label>
                    <Input
                      id="nome_responsavel"
                      value={formData.nome_responsavel}
                      onChange={(e) => setFormData(prev => ({ ...prev, nome_responsavel: e.target.value }))}
                      placeholder="Nome completo do responsável"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cpf_responsavel">CPF do Responsável *</Label>
                    <Input
                      id="cpf_responsavel"
                      value={formData.cpf_responsavel}
                      onChange={(e) => {
                        // Apenas números
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData(prev => ({ ...prev, cpf_responsavel: value }));
                      }}
                      placeholder="00000000000"
                      maxLength={11}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email_responsavel">E-mail do Responsável *</Label>
                    <Input
                      id="email_responsavel"
                      type="email"
                      value={formData.email_responsavel}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_responsavel: e.target.value }))}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="proposta" className="space-y-4">
          <div>
            <Label>Modelos de Proposta * ({formData.modelos_proposta_selecionados.length} selecionados)</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
              {modelosPropostas.map(modelo => (
                <div key={modelo.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`proposta-${modelo.id}`}
                    checked={formData.modelos_proposta_selecionados.includes(modelo.id)}
                    onCheckedChange={() => handleModeloPropostaToggle(modelo.id)}
                  />
                  <Label 
                    htmlFor={`proposta-${modelo.id}`}
                    className="cursor-pointer flex-1"
                  >
                    {modelo.nome_modelo}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {propostaPersonalizada && formData.modelos_proposta_selecionados.length > 0 && (
            <div className="mt-6">
              <Label>Pré-visualização das Propostas Selecionadas ({formData.modelos_proposta_selecionados.length})</Label>
              <div className="border rounded p-4 max-h-96 overflow-y-auto bg-gray-50">
                <div 
                  dangerouslySetInnerHTML={{ __html: propostaPersonalizada }}
                  className="prose prose-sm max-w-none"
                />
              </div>
            </div>
          )}
          
        </TabsContent>

        <TabsContent value="vale_transporte" className="space-y-4">
          <ValeTransporteForm
            data={formData.valeTransporteData}
            onChange={(data) => setFormData(prev => ({ ...prev, valeTransporteData: data }))}
          />
        </TabsContent>

        <TabsContent value="exame" className="space-y-4">
          <div>
            <Label htmlFor="tipo_exame">Tipo de Exame *</Label>
            <Select 
              value={formData.tipo_exame} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_exame: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo de exame" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admissional">Exame Admissional</SelectItem>
                <SelectItem value="psicotecnico">Exame Psicotécnico</SelectItem>
                <SelectItem value="clinico">Exame Clínico</SelectItem>
                <SelectItem value="laboratorial">Exame Laboratorial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="data_exame">Data do Exame</Label>
            <Input
              id="data_exame"
              type="date"
              value={formData.data_exame}
              onChange={(e) => setFormData(prev => ({ ...prev, data_exame: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="observacoes_exame">Observações do Exame</Label>
            <Textarea
              id="observacoes_exame"
              value={formData.observacoes_exame}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes_exame: e.target.value }))}
              placeholder="Observações sobre o exame..."
            />
          </div>
        </TabsContent>

        <TabsContent value="contrato" className="space-y-4">
          <div>
            <Label>Modelos de Contrato *</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
              {modelos.map(modelo => (
                <div key={modelo.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={modelo.id}
                    checked={formData.modelos_selecionados.includes(modelo.id)}
                    onCheckedChange={() => handleModeloToggle(modelo.id)}
                  />
                  <Label htmlFor={modelo.id} className="text-sm">
                    {modelo.nome_modelo}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="tipo_contrato">Tipo de Contrato</Label>
            <Select 
              value={formData.tipo_contrato} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_contrato: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo de contrato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="clt">CLT</SelectItem>
                <SelectItem value="pj">Pessoa Jurídica</SelectItem>
                <SelectItem value="estagio">Estágio</SelectItem>
                <SelectItem value="terceirizado">Terceirizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="observacoes_contrato">Observações do Contrato</Label>
            <Textarea
              id="observacoes_contrato"
              value={formData.observacoes_contrato}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes_contrato: e.target.value }))}
              placeholder="Observações sobre o contrato..."
            />
          </div>
        </TabsContent>

        <TabsContent value="integracao" className="space-y-4">
          <div>
            <Label htmlFor="quem_recebera_contratado">Quem irá receber o contratado *</Label>
            <Input
              id="quem_recebera_contratado"
              value={formData.quem_recebera_contratado}
              onChange={(e) => setFormData(prev => ({ ...prev, quem_recebera_contratado: e.target.value }))}
              placeholder="Nome da pessoa que irá receber o contratado"
            />
          </div>
          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              placeholder="Digite observações adicionais sobre a contratação..."
              rows={3}
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        
        <div className="space-x-2">
          {activeTab === 'proposta' ? (
            <Button 
              onClick={handleCriarPermissao}
              disabled={!isTabValid(activeTab) || loading}
            >
              {loading ? 'Criando Permissão...' : 'Criar Permissão'}
            </Button>
          ) : activeTab !== 'integracao' ? (
            <Button 
              onClick={handleSaveAndNext}
              disabled={!isTabValid(activeTab) || loading}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          ) : (
            <Button 
              onClick={handleFinish}
              disabled={!isTabValid(activeTab) || loading}
            >
              {loading ? 'Finalizando...' : 'Finalizar'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}